import React from 'react';
//Constants
import { EMPLOYMENT_TYPE, LABELS } from '../constants/general';
import { ResumeEmploymentDetailValidationSchema } from '../constants/schemas';
//Utils
import { addMultipleEmploymentDetails } from '../middleware/employmentDetails';
//Components
import Label from './Label';
//Utility Packages
import { FieldArray, Formik } from 'formik';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { classNames } from 'primereact/utils';
import DatePicker from 'react-datepicker';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { confirmDialog } from 'primereact/confirmdialog';
//Redux
import { dispatch } from '../utils/store';
import { useSelector } from 'react-redux';
import { Actions } from '../redux/actions';
// images
import deleteIcon from '../assets/images/grid-delete-icon.svg';

const toValidDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const EmploymentDetailForm = ({ isEditable, setIsEditable }) => {
  //Store
  const toastReff = useSelector((state) => state.notifications.toastReff);
  const { resumeData, employmentDetails } = useSelector((state) => state.resumeFetchDetails);
  const formatAddress = (address) => {
    return [
      address?.street,
      address?.area,
      address?.city,
      address?.state,
      address?.postalCode,
      address?.country
    ]
      .filter(Boolean)
      .join(', ');
  };
  const initialValues = {
    employments: resumeData.Experience.map((emp) => {
      const years = emp?.duration?.years ?? 0;
      const months = emp?.duration?.months ?? 0;
      const formattedEx = `${Math.floor(Number(years))}.${Math.floor(Number(months)).toString().padStart(2, '0')}`;
      return {
        jobTitle: emp?.role || '',
        currentEmployment: emp?.isCurrentEmployment ? '1' : '2',
        employmentType:
          EMPLOYMENT_TYPE.find(
            (item) => item.name.toLowerCase() === emp?.employmentType?.toLowerCase()
          )?.code || null,
        totalExperience: Number(formattedEx),
        companyName: emp?.company || '',
        jobLocation: formatAddress(emp?.companyAddress),
        startDate: toValidDate(emp.startDate),
        endDate: toValidDate(emp.endDate),
        joiningSalary: emp?.joiningSalary != null && !isNaN(parseFloat(emp.joiningSalary)) ? parseFloat(emp.joiningSalary) : null,
        lastSalary: emp?.lastSalary != null && !isNaN(parseFloat(emp.lastSalary)) ? parseFloat(emp.lastSalary) : null
      };
    })
  };
  const handleSubmit = (values) => {
    const handleSave = () => {
      dispatch(Actions.ResumeFetchDetails.SetEmploymentDetails, values);
      setIsEditable(true);
    };
    addMultipleEmploymentDetails(values?.employments, handleSave, toastReff);
  };
  const handleDeleteClick = (formik, index) => {
    confirmDialog({
      header: 'Delete',
      message: 'Are you sure you want to delete?',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'accept',
      accept: () => {
        const updatedProjects = formik.values.employments.filter((_, i) => i !== index);
        formik.setFieldValue('employments', updatedProjects);
      }
    });
  };
  return (
    <div className="resume-employeement-detail mt-3">
      <Formik
        initialValues={employmentDetails ? employmentDetails : initialValues}
        validationSchema={ResumeEmploymentDetailValidationSchema}
        enableReinitialize={true}
        onSubmit={(values) => handleSubmit(values)}>
        {(formik) => (
          <form onSubmit={formik.handleSubmit} className="p-fluid">
            <FieldArray name="employments">
              {() => (
                <>
                  {formik.values.employments.map((_, index) => (
                    <div key={index} className="project-title">
                      <div className="details-header">
                        <h3 className="mb-3">Employment {index + 1}</h3>
                        {formik.values.employments.length > 1 && !isEditable && (
                          <div>
                            <Button
                              type="button"
                              data-tooltip-id="delete-button"
                              data-tooltip-content="Delete"
                              rounded
                              outlined
                              severity="secondary"
                              aria-label="Delete"
                              onClick={() => handleDeleteClick(formik, index)}
                              className="grid-action-btn">
                              <em>
                                <img src={deleteIcon} alt="delete icon" />
                              </em>
                            </Button>
                            <ReactTooltip id="delete-button" place="top" />
                          </div>
                        )}
                      </div>
                      <EmploymentForm formik={formik} index={index} isEditable={isEditable} />
                      {index < formik.values.employments.length - 1 && <hr className="mt-7 mb-5" />}
                    </div>
                  ))}
                </>
              )}
            </FieldArray>
            <div className="submit-btn-block">
              {!isEditable && (
                <Button type="submit" variant="contained">
                  {LABELS.BUTTON.SAVE}
                </Button>
              )}
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};
const EmploymentForm = ({ formik, index, isEditable }) => {
  const field = (name) => `employments[${index}].${name}`;

  const getIn = (obj, path) => {
    return path
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
  };

  const isFormFieldValid = (formik, name) =>
    !!(getIn(formik.errors, name) && getIn(formik.touched, name));

  const getFormErrorMessage = (formik, name) => {
    const error = getIn(formik.errors, name);
    const touched = getIn(formik.touched, name);
    return touched && error ? <small className="p-error">{error}</small> : null;
  };

  return (
    <div className="form-row-wrapper">
      <div className="form-col form-col-3">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor="jobTitle" text={LABELS.INPUT.JOB_TITLE} isBold />
            <InputText
              id="jobTitle"
              name={field('jobTitle')}
              placeholder={LABELS.INPUT.JOB_TITLE}
              value={getIn(formik.values, field('jobTitle'))}
              onChange={formik.handleChange}
              disabled={isEditable}
              className={classNames({ 'p-invalid': isFormFieldValid(formik, field('jobTitle')) })}
            />
          </div>
          {getFormErrorMessage(formik, field('jobTitle'))}
        </div>
      </div>

      <div className="form-col form-col-3">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor="jobLocation" text={LABELS.INPUT.JOB_LOCATION} isBold />
            <InputText
              id="jobLocation"
              name={field('jobLocation')}
              placeholder={LABELS.INPUT.JOB_LOCATION}
              value={getIn(formik.values, field('jobLocation'))}
              onChange={formik.handleChange}
              disabled={isEditable}
              className={classNames({
                'p-invalid': isFormFieldValid(formik, field('jobLocation'))
              })}
            />
          </div>
          {getFormErrorMessage(formik, field('jobLocation'))}
        </div>
      </div>

      <div className="form-col form-col-3">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor="companyName" text={LABELS.INPUT.COMPANY_NAME} isBold />
            <InputText
              id="companyName"
              name={field('companyName')}
              placeholder={LABELS.INPUT.COMPANY_NAME}
              value={getIn(formik.values, field('companyName'))}
              onChange={formik.handleChange}
              disabled={isEditable}
              className={classNames({
                'p-invalid': isFormFieldValid(formik, field('companyName'))
              })}
            />
          </div>
          {getFormErrorMessage(formik, field('companyName'))}
        </div>
      </div>

      <div className="form-col form-col-3">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor="employmentType" text={LABELS.INPUT.EMPLOYMENT_TYPE} isBold />
            <Dropdown
              id="employmentType"
              name={field('employmentType')}
              value={getIn(formik.values, field('employmentType'))}
              optionLabel="name"
              optionValue="code"
              options={EMPLOYMENT_TYPE}
              onChange={formik.handleChange}
              panelClassName="custom-dropdown-panel"
              disabled={isEditable}
              className={classNames({
                'p-invalid': isFormFieldValid(formik, field('employmentType')),
                'w-full': true
              })}
            />
          </div>
          {getFormErrorMessage(formik, field('employmentType'))}
        </div>
      </div>

      <div className="form-col form-col-3">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor="totalExperience" text={LABELS.INPUT.TOTAL_EXP} isBold />
            <InputNumber
              id="totalExperience"
              name={field('totalExperience')}
              placeholder={LABELS.INPUT.TOTAL_EXP_PLACEHOLDER}
              value={getIn(formik.values, field('totalExperience'))}
              className={classNames({
                'p-invalid': isFormFieldValid(formik, field('totalExperience'))
              })}
              onValueChange={(e) => formik.setFieldValue(field('totalExperience'), e.value)}
              mode="decimal"
              minFractionDigits={2}
              maxFractionDigits={2}
              disabled={isEditable}
            />
          </div>
          {getFormErrorMessage(formik, field('totalExperience'))}
        </div>
      </div>

      <div className="form-col form-col-3">
        <div className="form-group-outer">
          <div className="custom-form-group margin-bottom">
            <Label htmlFor="currentEmployment" text={LABELS.INPUT.CURRENT_EMPLOYMENT} isBold />
            <div className="radio-list-wrapper">
              <div className="form-group-outer-radiobutton">
                <RadioButton
                  inputId="Yes"
                  value="1"
                  name={field('currentEmployment')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={getIn(formik.values, field('currentEmployment')) === '1'}
                  disabled={isEditable}
                />
                <Label htmlFor="Yes" text={LABELS.INPUT.YES} isBold />
              </div>
              <div className="form-group-outer-radiobutton">
                <RadioButton
                  inputId="No"
                  value="2"
                  name={field('currentEmployment')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  checked={getIn(formik.values, field('currentEmployment')) === '2'}
                  disabled={isEditable}
                />
                <Label htmlFor="No" text={LABELS.INPUT.NO} isBold />
              </div>
            </div>
          </div>
          {getFormErrorMessage(formik, field('currentEmployment'))}
        </div>
      </div>

      <div className="form-col form-col-4">
        <div className="form-group-outer">
          <div className="custom-form-group date-picker-wrapper">
            <Label htmlFor="startDate" text={LABELS.INPUT.START_DATE} isBold />
            <DatePicker
              id="startDate"
              name={field('startDate')}
              dateFormat="dd/MM/yyyy"
              placeholderText="Start Date"
              selected={getIn(formik.values, field('startDate'))}
              onChange={(date) => formik.setFieldValue(field('startDate'), date)}
              maxDate={new Date()}
              onKeyDown={(e) => e.preventDefault()}
              peekNextMonth
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              disabled={isEditable}
              className={classNames({
                'p-invalid': isFormFieldValid(formik, field('startDate'))
              })}
            />
          </div>
          {getFormErrorMessage(formik, field('startDate'))}
        </div>
      </div>

      <div className="form-col form-col-4">
        <div className="form-group-outer">
          <div className="custom-form-group date-picker-wrapper">
            <Label htmlFor="endDate" text={LABELS.INPUT.END_DATE} isBold />
            <DatePicker
              id="endDate"
              name={field('endDate')}
              dateFormat="dd/MM/yyyy"
              placeholderText="End Date"
              selected={getIn(formik.values, field('endDate'))}
              onChange={(date) => formik.setFieldValue(field('endDate'), date)}
              maxDate={new Date()}
              peekNextMonth
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onKeyDown={(e) => e.preventDefault()}
              disabled={isEditable}
              className={classNames({ 'p-invalid': isFormFieldValid(formik, field('endDate')) })}
            />
          </div>
          {getFormErrorMessage(formik, field('endDate'))}
        </div>
      </div>

      <div className="form-col form-col-4">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor="joiningSalary" text={LABELS.INPUT.JOINING_SALARY} isBold />
            <div className="p-inputgroup flex-1 salary-wrapper">
              <InputNumber
                id="joiningSalary"
                name={field('joiningSalary')}
                placeholder={LABELS.INPUT.JOINING_SALARY}
                value={getIn(formik.values, field('joiningSalary'))}
                className={classNames({
                  'p-invalid': isFormFieldValid(formik, field('joiningSalary'))
                })}
                onValueChange={(e) => formik.setFieldValue(field('joiningSalary'), e.value)}
                mode="decimal"
                min={0}
                max={100}
                minFractionDigits={1}
                maxFractionDigits={1}
                disabled={isEditable}
              />
              <span className="p-inputgroup-addon">LPA</span>
            </div>
          </div>
          {getFormErrorMessage(formik, field('joiningSalary'))}
        </div>
      </div>

      <div className="form-col form-col-4">
        <div className="form-group-outer">
          <div className="custom-form-group">
            <Label htmlFor="lastSalary" text={LABELS.INPUT.LAST_SALARY} isBold />
            <div className="p-inputgroup flex-1 salary-wrapper">
              <InputNumber
                id="lastSalary"
                name={field('lastSalary')}
                placeholder={LABELS.INPUT.JOINING_SALARY}
                value={getIn(formik.values, field('lastSalary'))}
                className={classNames({
                  'p-invalid': isFormFieldValid(formik, field('lastSalary'))
                })}
                onValueChange={(e) => formik.setFieldValue(field('lastSalary'), e.value)}
                mode="decimal"
                min={0}
                max={100}
                minFractionDigits={1}
                maxFractionDigits={1}
                disabled={isEditable}
              />
              <span className="p-inputgroup-addon">LPA</span>
            </div>
          </div>
          {getFormErrorMessage(formik, field('lastSalary'))}
        </div>
      </div>
    </div>
  );
};
export default EmploymentDetailForm;
